// src/pages/admin/categoryManagement.tsx - Enhanced Shopify-style design
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Pagination,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  Chip,
  Grid,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { Delete, Edit, Add, Search, FilterList, ArrowBack, Close } from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListCategoriesQuery,
  useDeleteCategoryMutation,
  useGetCategoryCountQuery,
  useAddCategoryMutation,
  useUpdateCategoryMutation,
  Category,
  CategoryFormData,
  FormErrors,
  SnackbarState,
} from '../../features/admin/categoryApi.ts';

const CategoryManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ 
    open: false, 
    message: '', 
    severity: 'success'
  });
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    cat_name: '',
    cat_short_name: '',
    cat_desc: '',
    status: 1,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const categoriesPerPage = 10;

  const { 
    data: categoriesResponse, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListCategoriesQuery({ 
    page, 
    limit: categoriesPerPage,
    search_str: searchStr
  });

  const {
    data: countResponse,
    refetch: refetchCount
  } = useGetCategoryCountQuery({ search_str: searchStr });

  const [deleteCategory, { isLoading: isDeleting }] = useDeleteCategoryMutation();
  const [addCategory] = useAddCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();

  const categories = categoriesResponse?.result || categoriesResponse?.data || [];
  const totalRecords = countResponse?.count || categoriesResponse?.count || 0;
  const totalPages = Math.ceil(totalRecords / categoriesPerPage);

  const isEditing = Boolean(editingCategory);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      refetchCount();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchStr, refetchCount]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const resetForm = () => {
    setFormData({
      cat_name: '',
      cat_short_name: '',
      cat_desc: '',
      status: 1,
    });
    setSelectedImage(null);
    setImagePreview('');
    setFormErrors({});
    setEditingCategory(null);
  };

  const handleAddCategory = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditCategory = (category: Category) => {
    setFormData({
      cat_name: category.cat_name || '',
      cat_short_name: category.cat_short_name || '',
      cat_desc: category.cat_desc || '',
      status: category.status || 1,
    });
    setImagePreview(category.cat_main_image ? 
      `https://fftestservice.sarsetech.com/images/${category.cat_main_image}` : '');
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleBackToList = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleStatusChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, status: Number(e.target.value) }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Please select a valid image file (JPEG, PNG, or GIF)',
          severity: 'error'
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: 'Image size should be less than 5MB',
          severity: 'error'
        });
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.cat_name.trim()) {
      newErrors.cat_name = 'Category name is required';
    }
    if (!formData.cat_short_name.trim()) {
      newErrors.cat_short_name = 'Category short name is required';
    }
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsFormSubmitting(true);

    try {
      const requestData: any = {
        cat_name: formData.cat_name.trim(),
        cat_short_name: formData.cat_short_name.trim(),
        status: formData.status ?? 1,
      };

      if (formData.cat_desc && formData.cat_desc.trim()) {
        requestData.cat_desc = formData.cat_desc.trim();
      }
      if (selectedImage) {
        requestData.cat_main_image = selectedImage;
      }
      if (isEditing && editingCategory) {
        requestData.cat_id = editingCategory.cat_id || editingCategory.id;
      }

      let result;
      if (isEditing) {
        result = await updateCategory(requestData).unwrap();
      } else {
        result = await addCategory(requestData).unwrap();
      }
      
      setSnackbar({
        open: true,
        message: result.message || `Category ${isEditing ? 'updated' : 'added'} successfully!`,
        severity: 'success'
      });
      
      handleBackToList();
      refetch();
      refetchCount();
    } catch (error: any) {
      console.error('Submit error:', error);
      setSnackbar({
        open: true,
        message: error.data?.message || error.message || `Error ${isEditing ? 'updating' : 'adding'} category.`,
        severity: 'error'
      });
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleDelete = async (categoryId: string | number) => {
  if (window.confirm('Are you sure you want to delete this category?')) {
    try {
      const result = await deleteCategory({ cat_id: Number(categoryId) }).unwrap();
      
      // Check if deletion was successful
      if (result.error) {
        // Handle specific error message from backend
        if (result.message.toLowerCase().includes('product mapped') || 
            result.message.toLowerCase().includes('cannot delete')) {
          setSnackbar({
            open: true,
            message: 'Cannot delete: This category has associated products. Please remove all products first.',
            severity: 'warning'
          });
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Error deleting category.',
            severity: 'error'
          });
        }
      } else {
        // Success case
        setSnackbar({
          open: true,
          message: result.message || 'Category deleted successfully!',
          severity: 'success'
        });
        refetch();
        refetchCount();
      }
    } catch (err: any) {
      console.error('Delete error details:', err);
      
      // Handle different types of errors
      if (err?.data?.message) {
        const errorMsg = err.data.message.toLowerCase();
        if (errorMsg.includes('product mapped') || errorMsg.includes('cannot delete')) {
          setSnackbar({
            open: true,
            message: 'Cannot delete: This category has associated products. Please remove all products first.',
            severity: 'warning'
          });
        } else {
          setSnackbar({
            open: true,
            message: err.data.message || 'Error deleting category.',
            severity: 'error'
          });
        }
      } else if (err?.message) {
        setSnackbar({
          open: true,
          message: err.message || 'Error deleting category.',
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Error deleting category.',
          severity: 'error'
        });
      }
    }
  }
};

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderStatus = (status: number) => {
    switch (status) {
      case 1:
        return <Chip label="Active" size="small" sx={{ bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600, fontSize: '12px' }} />;
      case 0:
        return <Chip label="Inactive" size="small" sx={{ bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600, fontSize: '12px' }} />;
      default:
        return <Chip label="Unknown" size="small" />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: 260, flexShrink: 0, display: { xs: 'none', md: 'block' }, height: '100vh', overflow: 'hidden' }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            {!showForm ? (
              // LIST VIEW
              <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Categories
                  </Typography>
                  
                  <Button
                    variant="contained"
                    onClick={handleAddCategory}
                    sx={{
                      bgcolor: '#202223',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#303030' }
                    }}
                  >
                    Add category
                  </Button>
                </Box>

                <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                  <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                    <Tabs 
                      value={tabValue} 
                      onChange={(e, v) => setTabValue(v)}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '14px',
                          minHeight: 48,
                          color: '#6d7175',
                        },
                        '& .Mui-selected': {
                          color: '#202223',
                          fontWeight: 600,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#202223',
                          height: 3,
                        }
                      }}
                    >
                      <Tab label={`All (${totalRecords})`} />
                      {/* <Tab label="Active" />
                      <Tab label="Inactive" /> */}
                    </Tabs>
                  </Box>

                  <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Search categories"
                      value={searchStr}
                      onChange={handleSearch}
                      fullWidth
                      sx={{ 
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          bgcolor: 'white',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: '#6d7175' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#c9cccf',
                        color: '#202223',
                        borderRadius: '8px',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#202223',
                          bgcolor: 'transparent',
                        }
                      }}
                    >
                      Filter
                    </Button>
                  </Box>

                  {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                      <CircularProgress size={32} sx={{ color: '#008060' }} />
                    </Box>
                  )}

                  {isError && !isLoading && (
                    <Box sx={{ p: 3, bgcolor: 'white' }}>
                      <Alert severity="error">Failed to load categories. Please try again.</Alert>
                    </Box>
                  )}

                  {!isLoading && !isError && categories.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        No categories found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchStr ? `No results for "${searchStr}"` : 'Get started by adding your first category'}
                      </Typography>
                    </Box>
                  )}

                  {!isLoading && categories.length > 0 && (
                    <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                      {/* Table Header */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '56px 88px minmax(180px, 1fr) 140px 120px 96px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>
                          Image
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Category
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Short Name
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>
                          Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>
                          Actions
                        </Typography>
                      </Box>

                      {/* Table Rows */}
                      {categories.map((category: Category, index: number) => (
                        <Box
                          key={category.cat_id ?? category.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '56px 88px minmax(180px, 1fr) 140px 120px 96px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: index < categories.length - 1 ? '1px solid #e1e3e5' : 'none',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#6d7175' }}>
                            {(page - 1) * categoriesPerPage + index + 1}
                          </Typography>

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <Avatar
                              src={category.cat_main_image ? 
                                `https://fftestservice.sarsetech.com/images/${category.cat_main_image}` : undefined}
                              variant="rounded"
                              sx={{ width: 48, height: 48, bgcolor: '#f1f2f4' }}
                            >
                            {category.cat_short_name?.[0] || category.cat_name?.[0] || '?'}
                            </Avatar>
                          </Box>

                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223', minWidth: 0, overflowWrap: 'anywhere' }}>
                            {category.cat_name || 'N/A'}
                          </Typography>

                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {category.cat_short_name || 'N/A'}
                          </Typography>

                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {renderStatus(category.status)}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditCategory(category)}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(category.cat_id ?? category.id)}
                              disabled={isDeleting}
                              sx={{ 
                                color: '#6d7175',
                                '&:hover': { bgcolor: '#f1f2f4' }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Card>

                {totalPages > 1 && !isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 500,
                        },
                        '& .Mui-selected': {
                          bgcolor: '#202223 !important',
                          color: 'white',
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              // FORM VIEW
              <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBackToList}
                    sx={{ 
                      color: '#202223',
                      textTransform: 'none',
                      fontWeight: 500,
                      mb: 2,
                      '&:hover': { bgcolor: 'transparent' }
                    }}
                  >
                    Categories
                  </Button>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    {isEditing ? 'Edit category' : 'Add category'}
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Stack spacing={2}>
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Category details
                            </Typography>
                            <TextField
                              name="cat_name"
                              label="Category name"
                              value={formData.cat_name}
                              onChange={handleInputChange}
                              error={!!formErrors.cat_name}
                              helperText={formErrors.cat_name}
                              fullWidth
                              required
                              placeholder="e.g., Home Appliances"
                              disabled={isFormSubmitting}
                              sx={{ mb: 2 }}
                            />

                            <TextField
                              name="cat_short_name"
                              label="Short name"
                              value={formData.cat_short_name}
                              onChange={handleInputChange}
                              error={!!formErrors.cat_short_name}
                              helperText={formErrors.cat_short_name || 'Maximum 10 characters'}
                              fullWidth
                              required
                              placeholder="e.g., HOP"
                              inputProps={{ maxLength: 10 }}
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Media
                            </Typography>
                            
                            <Box sx={{ 
                              border: '2px dashed #c9cccf', 
                              borderRadius: '12px', 
                              p: 4,
                              textAlign: 'center',
                              cursor: 'pointer',
                              bgcolor: '#fafbfb',
                              transition: 'all 0.2s',
                              '&:hover': { 
                                borderColor: '#202223',
                                bgcolor: '#f6f6f7'
                              }
                            }}
                            onClick={() => document.getElementById('image-upload')?.click()}>
                              {imagePreview ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <Avatar
                                    src={imagePreview}
                                    variant="rounded"
                                    sx={{ width: 120, height: 120, mx: 'auto' }}
                                  />
                                  <IconButton
                                    sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      bgcolor: 'white',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                      '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedImage(null);
                                      setImagePreview('');
                                    }}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box>
                                  <Box sx={{ 
                                    width: 56, 
                                    height: 56, 
                                    borderRadius: '8px',
                                    bgcolor: 'white',
                                    border: '1px solid #e1e3e5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 2
                                  }}>
                                    <Add sx={{ fontSize: 24, color: '#6d7175' }} />
                                  </Box>
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      textTransform: 'none',
                                      borderColor: '#c9cccf',
                                      color: '#202223',
                                      fontWeight: 500,
                                      borderRadius: '8px',
                                      '&:hover': {
                                        borderColor: '#202223',
                                        bgcolor: 'transparent',
                                      }
                                    }}
                                  >
                                    Add image
                                  </Button>
                                  <Typography variant="caption" display="block" sx={{ color: '#6d7175', mt: 1 }}>
                                    Accepts images, videos, or 3D models
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            <input
                              id="image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              style={{ display: 'none' }}
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>

                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                          <Box sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                              Description
                            </Typography>
                            <TextField
                              name="cat_desc"
                              value={formData.cat_desc}
                              onChange={handleInputChange}
                              fullWidth
                              multiline
                              rows={5}
                              placeholder="Enter category description"
                              disabled={isFormSubmitting}
                            />
                          </Box>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5' }}>
                        <Box sx={{ p: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                            Status
                          </Typography>
                          <FormControl fullWidth disabled={isFormSubmitting}>
                            <Select
                              value={formData.status.toString()}
                              onChange={handleStatusChange}
                              sx={{ borderRadius: '8px' }}
                            >
                              <MenuItem value={1}>Active</MenuItem>
                              <MenuItem value={0}>Inactive</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    justifyContent: 'flex-end', 
                    mt: 3,
                    pt: 3,
                    borderTop: '1px solid #e1e3e5'
                  }}>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#eeeeee',
                        bgcolor: '#f44336',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Close
                    </Button>
                    <Button
                      onClick={handleBackToList}
                      disabled={isFormSubmitting}
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 600,
                        color: '#eeeeee',
                        bgcolor: '#F78E12',
                        borderRadius: '8px',
                        px: 2.5
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isFormSubmitting || !formData.cat_name.trim() || !formData.cat_short_name.trim()}
                      sx={{
                        bgcolor: '#008060',
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' }
                      }}
                    >
                      {isFormSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} color="inherit" />
                          Saving...
                        </Box>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryManagement;